import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { ApiError, apiGet } from "./client";

/**
 * /api/v2/kpi/* — sozlesme §4 (backend/app/core/api/kpi.py).
 *
 *  - GET /kpi/snapshot: yoksa backend 404 `snapshot_yok` doner (bkz. kpi.py) —
 *    `useKpiSnapshot` bunu hata OLARAK degil `null` veri olarak yansitir
 *    (HealthRing/kartlar "—"/not_connected gosterir, sayfa kirilmaz).
 *  - GET /kpi/{kpi_id}/items: kpi.py'nin kasitli notu — bu satirlar loader'in
 *    HAM ciktisidir; ANCAK bazi kpi_id'lerde (kargolanmamis/kargo_yolda/
 *    destek_talepleri/bugun_destek) `level`/`working_hours`/`wait_hours`
 *    (SLA rozeti/sure — `_annotate_sla`), `admin_link` (Ticimax derin baglanti)
 *    ve `encok_satan` icin `rank` alanlari da BACKEND'DE eklenir (KPI detay
 *    modallerini eski arayuze esitleme gorevi, bkz. backend/app/core/api/kpi.py
 *    modul docstring'i). Satirlar hala KPI'ya gore degisen serbest sekilli
 *    oldugundan semada `z.record` ile gevsek tutulur.
 *  - DUZELTME TURU: `kpis` sozlugunun degerleri sadece obje/null DEGIL —
 *    backend/app/core/kpi/snapshot.py:154 `kpis["health_score"] = score`
 *    yazarak toplam saglik skorunu (duz SAYI) ayni sozlugun icine de gomer,
 *    ve backend/app/core/api/kpi.py:86 bunu oldugu gibi geciriyor. Deger
 *    semasi bu yuzden `number`'i de kabul eder (transform KULLANILMAZ —
 *    `apiRequest`'in `z.ZodType<T>` imzasi Input!=Output semalarda T'yi
 *    yanlis cikarabilir; bkz. client.ts, PARALEL paket dosyasi, DOKUNULMAZ).
 *    Asagi akis (features/kpi/health.ts, periodFilter.ts) zaten
 *    `typeof kpi !== 'object'` koruma satiriyla bu anahtari yok sayar.
 */

export const kpiEntrySchema = z.record(z.string(), z.unknown());
export type KpiEntry = Record<string, unknown>;

export const kpiSnapshotSchema = z.object({
  generated_at: z.string().nullable(),
  kpis: z.record(z.string(), z.union([kpiEntrySchema, z.number()]).nullable()),
  health_score: z.number().nullable(),
  health_score_breakdown: z.array(z.record(z.string(), z.unknown())).nullable().optional(),
});
export type KpiSnapshot = z.infer<typeof kpiSnapshotSchema>;

export const kpiItemsResponseSchema = z.object({
  status: z.string().optional(),
  kpi_id: z.string(),
  title: z.string(),
  items: z.array(kpiEntrySchema),
  rows: z.array(kpiEntrySchema),
  by_cargo_company: z.array(z.object({ cargo_company: z.string(), count: z.number() })),
  alarm_level: z.string().nullable(),
  count: z.number(),
});
export type KpiItemsResponse = z.infer<typeof kpiItemsResponseSchema>;

export const kpiHistoryEntrySchema = z.object({
  generated_at: z.string(),
  health_score: z.number().nullable(),
  kpis: z.record(z.string(), z.number().nullable()),
});
export const kpiHistoryResponseSchema = z.object({
  hours: z.number(),
  history: z.array(kpiHistoryEntrySchema),
});
export type KpiHistoryResponse = z.infer<typeof kpiHistoryResponseSchema>;

export function fetchKpiSnapshot(): Promise<KpiSnapshot> {
  return apiGet("/api/v2/kpi/snapshot", kpiSnapshotSchema);
}

export function fetchKpiItems(kpiId: string): Promise<KpiItemsResponse> {
  return apiGet(`/api/v2/kpi/${kpiId}/items`, kpiItemsResponseSchema);
}

export function fetchKpiHistory(hours = 24): Promise<KpiHistoryResponse> {
  return apiGet(`/api/v2/kpi/history?hours=${hours}`, kpiHistoryResponseSchema);
}

export const KPI_SNAPSHOT_QUERY_KEY = ["kpi", "snapshot"] as const;

export function useKpiSnapshot() {
  return useQuery({
    queryKey: KPI_SNAPSHOT_QUERY_KEY,
    queryFn: async (): Promise<KpiSnapshot | null> => {
      try {
        return await fetchKpiSnapshot();
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // Eski refresh.js: sekme acikken 15 dk'da bir arka plan yenileme (duvar ekrani).
    refetchInterval: 15 * 60_000,
  });
}

export function useKpiItems(kpiId: string | null) {
  return useQuery({
    queryKey: ["kpi", "items", kpiId],
    queryFn: () => fetchKpiItems(kpiId as string),
    enabled: kpiId != null,
  });
}

export function useKpiHistory(hours = 24) {
  return useQuery({
    queryKey: ["kpi", "history", hours],
    queryFn: () => fetchKpiHistory(hours),
  });
}
